import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { get } from 'rf-lib';
import { useField } from 'formik';
import { FormField } from 'rf-ui';

const Container = styled.div`
  display: flex;
  flex-direction: inherit;
  flex-wrap: wrap;
  grid-template-columns: 1fr 1fr 1fr;
  grid-column-gap: 10px;
  grid-gap: 20px;
  margin: ${get('spacingSm')} 0 ${get('spacingMd')} 0;
`;

const AdvancedContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  width: 100%;
  margin-top: ${get('spacingMd')};
`;

const FieldContainer = styled.div`
  flex: 0 0 220px;
  width: 220px;
`;

const SelectContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin: ${get('spacingSm')} 0 ${get('spacingMd')} 0;
`;

const SelectLabel = styled.label`
  margin-bottom: 0.4rem;
`;

const SelectInput = styled.select`
  border-radius: 3px;
  padding: 0.6rem;
  height: auto;
`;

const CheckboxRow = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 0.6rem;
  margin: ${get('spacingSm')} 0 ${get('spacingMd')} 0;
`;

const CheckboxInput = styled.input`
  width: 1.4rem;
  height: 1.4rem;
  margin: 0;
`;

export const RANDOMNESS_OPTIONS = [
  { value: 1, description: 'Formell' },
  { value: 2, description: 'Standard' },
  { value: 3, description: 'Gøy' },
  { value: 4, description: 'Crazy' },
  { value: 5, description: 'Crazy Solea edition' }
];

export const SPIN_SPEED_OPTIONS = [
  { value: 1, description: 'Veldig sakte - ca 15sec' },
  { value: 2, description: 'Sakte - ca 12sec' },
  { value: 3, description: 'Normal - ca 10sec' },
  { value: 4, description: 'Rask - ca 7sec' },
  { value: 5, description: 'Veldig rask - ca 4sec' }
];

const SelectField = ({ name, title, options }) => {
  const [field] = useField(name);

  return (
    <SelectContainer>
      <SelectLabel htmlFor={name}>{title}</SelectLabel>
      <SelectInput id={name} {...field} value={field.value ?? ''}>
        <option value="">Velg nivå</option>
        {options.map(option => (
          <option key={option.value} value={option.value}>
            {`${option.value} - ${option.description}`}
          </option>
        ))}
      </SelectInput>
    </SelectContainer>
  );
};

SelectField.propTypes = {
  name: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  options: PropTypes.array.isRequired,
};

const BoolCheckboxField = ({ name, label }) => {
  const [field] = useField({ name, type: 'checkbox' });

  return (
    <CheckboxRow htmlFor={name}>
      <CheckboxInput id={name} type="checkbox" {...field} checked={Boolean(field.value)} />
      {label}
    </CheckboxRow>
  );
};

BoolCheckboxField.propTypes = {
  name: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
};

const GameFormDetails = ({
  items, onChange, showAdvancedSettings = false, ...props
}) => (
  <>
    <Container>
      <FormField
        name="maxTickets"
        type="number"
        min="1"
        title="Maks lodd"
  />
      <FormField
        name="maxTicketsPerPlayer"
        type="number"
        min="1"
        title="Maks lodd per spiller"
  />
      <FormField
        name="numberOfPrizes"
        type="number"
        min="1"
        title="Antall premier"
  />
      <FormField
        name="ticketPrice"
        type="number"
        min="0"
        title="Pris per lodd"
  />
      <BoolCheckboxField
        name="prohibitNegativeBalance"
        label="Nekt negativ saldo"
      />
    </Container>
    {showAdvancedSettings && (
      <>
        <h1>Avanserte innstillinger</h1>
        <AdvancedContainer>
          <FieldContainer>
            <SelectField
              name="randomness"
              title="Spenningsnivå"
              options={RANDOMNESS_OPTIONS}
            />
          </FieldContainer>
          <FieldContainer>
            <SelectField
              name="spinSpeed"
              title="Spinnhastighet"
              options={SPIN_SPEED_OPTIONS}
            />
          </FieldContainer>
        </AdvancedContainer>
      </>
    )}
  </>
);

GameFormDetails.propTypes = {
  items: PropTypes.array,
  onChange: PropTypes.func,
  showAdvancedSettings: PropTypes.bool,
};

export default GameFormDetails;
